import { Alert, Badge, Button, Link, Spinner, Tag } from '@1k5-tech-es/grado-react';
import { Info, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router';

import type { QuarterSummary } from '../../api/types';
import { buildChecks, type DataCheck } from './quarters';

const VISIBLE_NAMES = 6;

/** Gaps in the quarter's data, explained by how they change the PDF. Never blocks generation. */
export function DataReview({ summary }: { summary: QuarterSummary | undefined }) {
  if (!summary) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Spinner size="sm" decorative /> Revisando los datos…
      </div>
    );
  }

  const checks = buildChecks(summary);
  if (checks.length === 0) {
    return (
      <Alert tone="success" title="Todo listo">
        No falta ningún dato que cambie los informes de este trimestre.
      </Alert>
    );
  }

  const warnings = checks.filter((c) => c.tone === 'warning').length;
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {warnings > 0 && <span className="font-semibold text-warning-text">{warnings} que afectan a cifras o secciones · </span>}
        {checks.length - warnings > 0 && `${checks.length - warnings} informativos · `}
        Puedes generar igualmente: solo explican cómo saldrá cada PDF.
      </p>
      <ul className="mt-3 space-y-2">
        {checks.map((check) => (
          <CheckItem key={check.id} check={check} total={summary.advisorCount} />
        ))}
      </ul>
    </>
  );
}

function CheckItem({ check, total }: { check: DataCheck; total: number }) {
  const [expanded, setExpanded] = useState(false);
  const warning = check.tone === 'warning';
  const names = expanded ? check.advisors : check.advisors.slice(0, VISIBLE_NAMES);
  const hidden = check.advisors.length - names.length;
  const Icon = warning ? TriangleAlert : Info;

  return (
    <li className={`rounded-lg border px-4 py-3 ${warning ? 'border-warning-border bg-warning-subtle' : 'border-border bg-card'}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex min-w-0 flex-1 gap-3">
          <Icon aria-hidden className={`mt-0.5 size-4 shrink-0 ${warning ? 'text-warning-text' : 'text-muted-foreground'}`} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
              {check.title}
              <Badge tone={warning ? 'warning' : 'neutral'}>
                {check.everyone ? `Todos (${total})` : check.advisors.length === 1 ? '1 asesor' : `${check.advisors.length} asesores`}
              </Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground">{check.consequence}</p>
            {!check.everyone && (
              <div className="mt-2 flex flex-wrap items-center gap-1">
                {names.map((name) => (
                  <Tag key={name} size="sm">
                    {name}
                  </Tag>
                ))}
                {hidden > 0 && (
                  <Button variant="link" size="sm" onClick={() => setExpanded(true)}>
                    +{hidden} más
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        <Link as={RouterLink} to={check.fix.to} className="shrink-0 pl-7 text-xs font-semibold whitespace-nowrap sm:pl-0">
          {check.fix.label} →
        </Link>
      </div>
    </li>
  );
}
